import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Loader2, Copy, Check, X, AlertTriangle, ExternalLink } from "lucide-react";
import { toast } from "react-toastify";
import {
  STAGING_FORM_URL,
  STAGER_NAME,
  STAGER_EMAIL,
  SIZE_OPTIONS,
  PAYER_OPTIONS,
  BUDGET_OPTIONS,
  ROOM_OPTIONS,
  INQUIRY_OPTIONS,
  STATUS_OPTIONS,
  STAGING_FIELDS,
  buildStagingPayload,
} from "../../utils/stagingIntake";
import { buildFillerBookmarklet } from "../../utils/stagingFiller";
import { getAllusers, getContactsByIds } from "../../utils/firebasefunctions";
import { normalizeContact } from "../../utils/contactNormalize";

// Request a staging bid from Register Redesign without retyping the file.
//
// Three steps: answer the four things the file can't know, hand the answers to
// her form, then tell TES whether it actually went through. TES can't see her
// form, so the last step asks rather than assuming.

const LABEL = "text-[10px] font-bold uppercase tracking-wider text-slate-400";
const SELECT =
  "w-full rounded-md border border-slate-300 px-2 py-1.5 text-[12.5px] text-slate-800 focus:border-themeColor focus:outline-none";

const todayIso = () => {
  const d = new Date();
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

export default function RequestStagingBidModal({
  open,
  onClose,
  deal,
  formData,
  onFormChange,
  clients,
  currentUser,
}) {
  const [step, setStep] = useState("form");
  const [showSetup, setShowSetup] = useState(false);
  const [loading, setLoading] = useState(true);
  // React strips javascript: hrefs, so the bookmarklet is attached to the DOM
  // node directly. It's a link you drag once, not one you click here.
  const bookmarkRef = useRef(null);
  const [agent, setAgent] = useState(null);
  const [people, setPeople] = useState([]);

  const [size, setSize] = useState("");
  const [payer, setPayer] = useState("");
  const [budget, setBudget] = useState("");
  const [rooms, setRooms] = useState([]);
  const [notes, setNotes] = useState("");
  // Answers typed in for questions this listing file couldn't fill, keyed by
  // Sharon's field name. Real files are routinely missing occupancy, a list
  // date or a client email — those used to reach her blank.
  const [overrides, setOverrides] = useState({});

  // Resolve the two things the deal only stores as ids: the assigned agent and
  // the clients. Sharon replies to the listing agent, not whoever clicked the
  // button, so an assistant requesting on their behalf still routes correctly.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        let resolvedAgent = null;
        if (deal?.agentData) {
          const users = await getAllusers("users");
          resolvedAgent = (users || []).find((u) => u.id === deal.agentData);
        }

        const named = (clients || []).filter((c) =>
          (c?.fullName || c?.name || "").trim()
        );
        let resolvedPeople = named.map((c) => ({
          name: (c.fullName || c.name || "").trim(),
          email: c.email || "",
        }));
        if (!resolvedPeople.length && deal?.customers?.length) {
          const contacts = await getContactsByIds(deal.customers);
          resolvedPeople = (contacts || [])
            .map(normalizeContact)
            .map((c) => ({ name: c.name, email: c.email }));
        }

        if (cancelled) return;
        setAgent(resolvedAgent || currentUser || null);
        setPeople(resolvedPeople);
      } catch (e) {
        if (!cancelled) {
          setAgent(currentUser || null);
          toast.error("Couldn't load the agent and client for this file");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, deal, clients, currentUser]);

  useEffect(() => {
    if (open) {
      setStep("form");
      setShowSetup(false);
      setOverrides({});
    }
  }, [open]);

  useEffect(() => {
    if (bookmarkRef.current) {
      bookmarkRef.current.setAttribute("href", buildFillerBookmarklet());
    }
  }, [showSetup, step]);

  const payload = useMemo(
    () =>
      buildStagingPayload({
        deal: { ...(deal || {}), ...(formData || {}) },
        agent,
        clients: people,
        answers: { size, payer, budget, rooms, notes },
        overrides,
      }),
    [deal, formData, agent, people, size, payer, budget, rooms, notes, overrides]
  );

  // Nothing should reach Sharon blank, so every gap has to be filled in here.
  const answered =
    size && payer && budget && rooms.length && payload.missing.length === 0;

  const setOverride = (name, value) =>
    setOverrides((prev) => ({ ...prev, [name]: value }));

  // The questions the file couldn't answer, in her order, with the right input.
  // What the FILE itself cannot answer, deliberately computed with NO overrides.
  // Tracking payload.missing here would delete a field the instant you typed the
  // first character into it, because the field stops being "missing" the moment
  // it holds anything. The input would unmount mid-typing and keep that single
  // character as the answer. The gap list has to stay fixed while you fill it in.
  const fileMissing = useMemo(
    () =>
      buildStagingPayload({
        deal: { ...(deal || {}), ...(formData || {}) },
        agent,
        clients: people,
        answers: { size, payer, budget, rooms, notes },
        overrides: {},
      }).missing,
    [deal, formData, agent, people, size, payer, budget, rooms, notes]
  );

  const gaps = useMemo(() => {
    const optionsFor = {
      q27029954: INQUIRY_OPTIONS,
      q27029955: STATUS_OPTIONS,
    };
    return STAGING_FIELDS.filter((f) => fileMissing.includes(f.label)).map(
      (f) => ({ ...f, options: optionsFor[f.name] || null })
    );
  }, [fileMissing]);

  const toggleRoom = (r) =>
    setRooms((prev) =>
      prev.includes(r) ? prev.filter((x) => x !== r) : [...prev, r]
    );

  const handleCopyAndOpen = useCallback(async () => {
    const text = JSON.stringify(payload);

    // The answers ride over as the new tab's NAME. Her site denies
    // clipboard-read, so the clipboard can't be the delivery route — and a
    // query string is out, since this carries the client's name and email.
    // The bookmarklet reads the name and wipes it.
    //
    // noopener is deliberately absent: it nulls the returned handle, and we
    // need that handle to tell a blocked popup from a real one.
    const tab = window.open(STAGING_FORM_URL, text);

    if (!tab) {
      toast.error(
        "Your browser blocked the new tab. Allow pop-ups for TES and try again."
      );
      return;
    }

    // Best-effort copy so the paste fallback works if the tab gets reopened.
    try {
      await navigator.clipboard.writeText(text);
    } catch (e) {
      /* not fatal — the tab name is the real channel */
    }

    setStep("handoff");
  }, [payload]);

  const handleMarkSent = useCallback(() => {
    onFormChange?.("stagingProvider", STAGER_NAME);
    onFormChange?.("stagingProviderEmail", STAGER_EMAIL);
    onFormChange?.("stagingBid", todayIso());
    toast.success("Staging bid logged on this file");
    onClose?.();
  }, [onFormChange, onClose]);

  if (!open) return null;

  const autoFilled = payload.fields.filter((f) => f.source !== "You");

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4">
      <div className="max-h-[92vh] w-full max-w-2xl overflow-y-auto rounded-xl bg-white shadow-xl">
        <div className="flex items-start justify-between border-b border-slate-200 px-5 py-4">
          <div>
            <div className="text-[15px] font-semibold text-slate-900">
              {step === "form" && "Request staging bid"}
              {step === "handoff" && "Her form is open in the other tab"}
              {step === "confirm" && "Update the file?"}
            </div>
            <div className="mt-0.5 text-[12px] text-slate-500">
              {STAGER_NAME}
              {deal?.propertyaddress ? ` · ${deal.propertyaddress}` : ""}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        {step === "form" && (
          <>
            <div className="px-5 py-4">
              {loading ? (
                <div className="flex items-center gap-2 py-6 text-[12.5px] text-slate-500">
                  <Loader2 size={14} className="animate-spin" /> Reading the file…
                </div>
              ) : (
                <>
                  <div className="mb-2 flex items-center gap-1.5 text-[12px] font-medium text-emerald-700">
                    <Check size={14} /> {autoFilled.length} answers pulled from
                    this file
                  </div>
                  <div className="rounded-lg bg-slate-50 px-4 py-3">
                    <table className="w-full text-[12.5px]">
                      <tbody>
                        {autoFilled.map((f) => (
                          <tr key={f.name}>
                            <td className="py-[3px] pr-3 align-top text-slate-500">
                              {f.label}
                            </td>
                            <td className="py-[3px] text-right align-top font-medium text-slate-800">
                              {Array.isArray(f.value) ? f.value.join(", ") : f.value}
                              <span className="ml-2 text-[10.5px] font-normal text-slate-400">
                                {f.source}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {gaps.length > 0 && (
                    <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
                      <div className="mb-2 flex items-start gap-2">
                        <AlertTriangle
                          size={13}
                          className="mt-0.5 shrink-0 text-amber-600"
                        />
                        <p className="text-[11.5px] leading-relaxed text-amber-800">
                          This file doesn't have{" "}
                          {gaps.length === 1 ? "this" : `these ${gaps.length}`}.
                          Fill {gaps.length === 1 ? "it" : "them"} in here rather
                          than leaving {gaps.length === 1 ? "it" : "them"} blank
                          on her form.
                        </p>
                      </div>
                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        {gaps.map((g) => (
                          <div
                            key={g.name}
                            className={g.options ? "sm:col-span-2" : ""}
                          >
                            <div className={`${LABEL} mb-1`}>{g.label}</div>
                            {g.options ? (
                              <select
                                className={SELECT}
                                value={overrides[g.name] || ""}
                                onChange={(e) =>
                                  setOverride(g.name, e.target.value)
                                }
                              >
                                <option value="">Choose one</option>
                                {g.options.map((o) => (
                                  <option key={o} value={o}>
                                    {o}
                                  </option>
                                ))}
                              </select>
                            ) : (
                              <input
                                type="text"
                                className={SELECT}
                                value={overrides[g.name] || ""}
                                placeholder={
                                  g.name === "q27029961"
                                    ? "MM/DD/YYYY"
                                    : g.name === "q27029951"
                                    ? "(651) 555-0134"
                                    : ""
                                }
                                onChange={(e) =>
                                  setOverride(g.name, e.target.value)
                                }
                              />
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="mb-2 mt-5 text-[12px] font-medium text-slate-700">
                    Sharon needs 4 things the file doesn't have
                  </div>

                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <div className={`${LABEL} mb-1`}>Property size</div>
                      <select
                        className={SELECT}
                        value={size}
                        onChange={(e) => setSize(e.target.value)}
                      >
                        <option value="">Choose one</option>
                        {SIZE_OPTIONS.map((o) => (
                          <option key={o} value={o}>
                            {o}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <div className={`${LABEL} mb-1`}>Who is paying</div>
                      <select
                        className={SELECT}
                        value={payer}
                        onChange={(e) => setPayer(e.target.value)}
                      >
                        <option value="">Choose one</option>
                        {PAYER_OPTIONS.map((o) => (
                          <option key={o} value={o}>
                            {o}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <div className={`${LABEL} mb-1`}>Estimated budget</div>
                      <select
                        className={SELECT}
                        value={budget}
                        onChange={(e) => setBudget(e.target.value)}
                      >
                        <option value="">Choose one</option>
                        {BUDGET_OPTIONS.map((o) => (
                          <option key={o} value={o}>
                            {o}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="sm:col-span-2">
                      <div className={`${LABEL} mb-1`}>Rooms to stage</div>
                      <div className="flex flex-wrap gap-1.5">
                        {ROOM_OPTIONS.map((r) => {
                          const on = rooms.includes(r);
                          return (
                            <button
                              key={r}
                              type="button"
                              onClick={() => toggleRoom(r)}
                              className={
                                on
                                  ? "rounded-full border border-themeColor bg-slate-50 px-2.5 py-1 text-[11.5px] font-medium text-themeColor"
                                  : "rounded-full border border-slate-300 px-2.5 py-1 text-[11.5px] text-slate-600 hover:border-slate-400"
                              }
                            >
                              {r}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    <div className="sm:col-span-2">
                      <div className={`${LABEL} mb-1`}>
                        Anything else she should know{" "}
                        <span className="font-normal normal-case tracking-normal text-slate-400">
                          optional
                        </span>
                      </div>
                      <textarea
                        rows={2}
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder="Sellers have two large dogs. Basement is unfinished."
                        className={SELECT}
                      />
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className="flex items-center justify-between gap-3 border-t border-slate-200 px-5 py-3.5">
              <span className="text-[11.5px] text-slate-400">
                Opens her form in a new tab
              </span>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-md border border-slate-300 px-3 py-1.5 text-[12px] font-semibold text-slate-600 hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleCopyAndOpen}
                  disabled={loading || !answered}
                  className="inline-flex items-center gap-1.5 rounded-md bg-themeColor px-3 py-1.5 text-[12px] font-semibold text-white hover:bg-themeColor/90 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Copy size={13} /> Copy and open form
                </button>
              </div>
            </div>
          </>
        )}

        {step === "handoff" && (
          <>
            <div className="px-5 py-4">
              <ol className="space-y-3 text-[12.5px] text-slate-700">
                {[
                  <>
                    Switch to the new tab and click your{" "}
                    <span className="rounded border border-slate-300 bg-slate-50 px-1.5 py-0.5 font-medium">
                      Fill staging form
                    </span>{" "}
                    bookmark.
                  </>,
                  <>
                    Check what it filled in, then clear the "I'm not a robot"
                    box.
                  </>,
                  <>Press Submit on her form, then come back here.</>,
                ].map((line, i) => (
                  <li key={i} className="flex gap-3">
                    <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-slate-200 text-[11px] font-bold text-slate-600">
                      {i + 1}
                    </span>
                    <span>{line}</span>
                  </li>
                ))}
              </ol>

              <div className="mt-4 flex items-start gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                <AlertTriangle size={13} className="mt-0.5 shrink-0 text-slate-500" />
                <p className="text-[11.5px] leading-relaxed text-slate-600">
                  The bookmark reports what it filled. If Sharon has changed a
                  question, it says which one and leaves it blank rather than
                  guessing, so finish those by hand before submitting.
                </p>
              </div>

              <button
                type="button"
                onClick={() => setShowSetup((s) => !s)}
                className="mt-3 text-[11.5px] font-semibold text-linkColor hover:text-themeColor"
              >
                {showSetup
                  ? "Hide setup"
                  : "Don't have the bookmark yet? Set it up once"}
              </button>

              {showSetup && (
                <div className="mt-2 rounded-lg border border-slate-200 px-4 py-3">
                  <ol className="mb-3 list-decimal space-y-1 pl-4 text-[11.5px] text-slate-600">
                    <li>
                      Show your bookmarks bar if it's hidden (Cmd + Shift + B).
                    </li>
                    <li>Drag the blue button below onto that bar.</li>
                    <li>That's it. It works for every listing from now on.</li>
                  </ol>
                  {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                  <a
                    ref={bookmarkRef}
                    draggable
                    onClick={(e) => e.preventDefault()}
                    className="inline-block cursor-grab rounded-md bg-themeColor px-3 py-1.5 text-[12px] font-semibold text-white"
                  >
                    Fill staging form
                  </a>
                  <p className="mt-2 text-[11px] text-slate-400">
                    Dragging saves it. Clicking it here does nothing.
                  </p>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 border-t border-slate-200 px-5 py-3.5">
              <button
                type="button"
                onClick={() => setStep("form")}
                className="rounded-md border border-slate-300 px-3 py-1.5 text-[12px] font-semibold text-slate-600 hover:bg-slate-50"
              >
                Back
              </button>
              <button
                type="button"
                onClick={() => setStep("confirm")}
                className="inline-flex items-center gap-1.5 rounded-md bg-themeColor px-3 py-1.5 text-[12px] font-semibold text-white hover:bg-themeColor/90"
              >
                <ExternalLink size={13} /> I submitted it
              </button>
            </div>
          </>
        )}

        {step === "confirm" && (
          <>
            <div className="px-5 py-4">
              <p className="mb-3 text-[12.5px] text-slate-600">
                TES can't see Sharon's form, so it's asking rather than assuming.
                Saying yes writes:
              </p>
              <div className="rounded-lg bg-slate-50 px-4 py-3 text-[12.5px]">
                {[
                  ["Stager", STAGER_NAME],
                  ["Stager email", STAGER_EMAIL],
                  ["Bid requested", todayIso()],
                ].map(([k, v]) => (
                  <div key={k} className="flex justify-between py-[3px]">
                    <span className="text-slate-500">{k}</span>
                    <span className="font-medium text-slate-800">{v}</span>
                  </div>
                ))}
              </div>
              <p className="mt-3 text-[11.5px] text-slate-400">
                Save the file afterwards to keep this.
              </p>
            </div>
            <div className="flex justify-end gap-2 border-t border-slate-200 px-5 py-3.5">
              <button
                type="button"
                onClick={onClose}
                className="rounded-md border border-slate-300 px-3 py-1.5 text-[12px] font-semibold text-slate-600 hover:bg-slate-50"
              >
                Not yet
              </button>
              <button
                type="button"
                onClick={handleMarkSent}
                className="rounded-md bg-themeColor px-3 py-1.5 text-[12px] font-semibold text-white hover:bg-themeColor/90"
              >
                Yes, update the file
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
